import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DerivationWizard } from '../components/psychologist/patients/derivation/DerivationWizard';
import apiCoreAdapter from '../hooks/apiCoreAdapter';
import CircularProgress from '../components/common/CircularProgress';
import { useIsPwaApp } from '../pwa/PwaModeContext';

/**
 * `patient_email`/`patient`/`psychologist_email` YA NO viajan en la URL (mismo query string
 * que usaba antes el iframe de Tally, editable a mano por cualquiera). Solo `process_id`
 * (para el lookup) y `total_sessions` (grado A-D, no sensible) siguen en el query string —
 * el resto se resuelve autenticado por `process_id` vía
 * GET /psychologist/derivation-submissions/context/:processId, que además verifica que el
 * proceso pertenezca a la psicóloga logueada antes de devolver nada.
 */
export const Derivaciones: React.FC = () => {
  const navigate = useNavigate();
  // App mobile: la barra superior es más alta (incluye el área segura del iPhone) que la de la web.
  const isPwaApp = useIsPwaApp();
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);
  const processId = params.get('process_id') || '';
  const totalSessionsGrade = params.get('total_sessions') || '';

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<{
    patientEmail: string;
    patientName: string;
    psychologistEmail: string;
  } | null>(null);
  const [contextError, setContextError] = useState(false);

  useEffect(() => {
    if (!processId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const result = await apiCoreAdapter.getDerivationSubmitContext(processId);
        if (!cancelled) setContext(result);
      } catch {
        // beHttp ya mostró el toast de error (proceso inexistente o de otra psicóloga).
        if (!cancelled) setContextError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [processId]);

  const goBack = () => { window.history.length > 1 ? navigate(-1) : navigate('/patients'); };

  const missingParams = !processId || contextError || (!loading && !context);

  return (
    <>
      <button
        type="button"
        onClick={goBack}
        style={{
          position: 'fixed',
          top: isPwaApp ? 'calc(4rem + env(safe-area-inset-top) + 0.5rem)' : '4.5rem',
          left: '1rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.5rem 1rem',
          background: '#fff',
          color: '#374151',
          border: '1px solid #d1d5db',
          borderRadius: '9999px',
          fontSize: '0.875rem',
          fontWeight: 700,
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 200,
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#9ca3af'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#d1d5db'; }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Volver
      </button>
      <div className={`mx-auto max-w-[1000px] px-4 pb-16 ${isPwaApp ? "pt-16" : "pt-20"}`}>
        {loading ? (
          <div className="flex justify-center py-16">
            <CircularProgress label="Cargando formulario de derivación…" />
          </div>
        ) : missingParams ? (
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-white p-8 text-center shadow-sm">
            <h3 className="text-lg font-bold text-foreground">No pudimos abrir el formulario</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Faltan datos del paciente o del proceso para iniciar la derivación. Volvé a la ficha del
              paciente e intentá de nuevo desde ahí.
            </p>
          </div>
        ) : submitted ? (
          <div className="mx-auto max-w-2xl rounded-2xl border border-border bg-white p-8 text-center shadow-sm">
            <h3 className="text-lg font-bold text-foreground">Solicitud enviada</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Tu solicitud de derivación fue enviada correctamente. Te contactaremos a la brevedad con la
              resolución.
            </p>
          </div>
        ) : context ? (
          <DerivationWizard
            processId={processId}
            patientEmail={context.patientEmail}
            patientName={context.patientName}
            psychologistEmail={context.psychologistEmail}
            sessionsCountGrade={totalSessionsGrade}
            onSubmitted={() => setSubmitted(true)}
          />
        ) : null}
      </div>
    </>
  );
};

export default Derivaciones;
